/**
 * Pieces only the creator screens use: a bottom sheet, a switch, a tag
 * picker, a photo picker, the AI helper, and the card a brand sees.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { api, ApiError } from '../lib/api';
import { useI18n, useT } from '../lib/i18n';
import { compressImage } from '../lib/image';
import {
  PLATFORMS,
  compactCount,
  copyText,
  isVerified,
  nicheLabel,
  platformLabel,
  uploadFile,
  type CreatorChannel,
  type CreatorProfile,
} from '../lib/creator';
import { formatINR } from '../lib/format';
import { Icon } from './Icon';

/**
 * A sheet that slides up from the bottom. Rendered into <body> so no
 * transformed ancestor (the page's rise animation) can trap it, and it carries
 * its own section colours because the portal leaves the page's data-sec.
 */
export function Sheet({
  open,
  onClose,
  title,
  sec,
  children,
  footer,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  sec: string;
  children: ReactNode;
  footer?: ReactNode;
}) {
  const T = useT();
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);
  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center" data-sec={sec}>
      <button
        type="button"
        aria-label={T('बंद करें', 'Close')}
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px]"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="rise relative flex max-h-[92vh] w-full max-w-md flex-col rounded-t-[28px] bg-panel shadow-lift"
      >
        <div className="flex items-center justify-between gap-3 border-b border-line px-5 py-3">
          <h2 className="font-display text-lg font-bold">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={T('बंद करें', 'Close')}
            className="-mr-2 flex h-11 w-11 items-center justify-center rounded-full text-muted hover:bg-panel-2"
          >
            <Icon name="x" className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
        {footer && <div className="safe-bottom border-t border-line px-5 pt-3">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}

/** An on/off switch with a label and one line on what it means. */
export function Toggle({
  checked,
  onChange,
  label,
  hint,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  hint?: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className="flex w-full items-center gap-3 rounded-2xl text-left"
    >
      <span className="min-w-0 flex-1">
        <span className="block font-semibold">{label}</span>
        {hint && <span className="block text-xs text-muted">{hint}</span>}
      </span>
      <span
        aria-hidden
        className={`relative h-7 w-12 shrink-0 rounded-full transition ${checked ? '' : 'bg-line'}`}
        style={
          checked
            ? { backgroundImage: 'linear-gradient(135deg, var(--sec-from), var(--sec-to))' }
            : undefined
        }
      >
        <span
          className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow transition-all ${
            checked ? 'left-[22px]' : 'left-0.5'
          }`}
        />
      </span>
    </button>
  );
}

/** One choice among a few, as a segmented row. */
export function Segmented<V extends string>({
  value,
  onChange,
  options,
}: {
  value: V;
  onChange: (v: V) => void;
  options: Array<[V, string]>;
}) {
  return (
    <div className="flex gap-1 rounded-2xl bg-panel-2 p-1">
      {options.map(([k, label]) => (
        <button
          key={k}
          type="button"
          onClick={() => onChange(k)}
          aria-pressed={value === k}
          className={`flex-1 rounded-xl px-2 text-sm font-semibold transition ${
            value === k ? 'bg-panel shadow-card' : 'text-muted'
          }`}
          style={value === k ? { color: 'var(--sec-ink)' } : undefined}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

/** A chip. Selected chips wear the section colour. */
export function Chip({
  on,
  onClick,
  children,
}: {
  on: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onClick}
      className={`inline-flex min-h-[40px] items-center gap-1 rounded-full border px-3.5 text-sm transition ${
        on ? 'font-semibold' : 'border-line bg-panel text-muted'
      }`}
      style={
        on
          ? {
              background: 'var(--sec-wash)',
              color: 'var(--sec-ink)',
              borderColor: 'var(--sec-ink)',
            }
          : undefined
      }
    >
      {on && <Icon name="check" className="h-3.5 w-3.5" strokeWidth={2.6} />}
      {children}
    </button>
  );
}

/**
 * Pick from a list, or type your own. Selected custom tags show first so a
 * creator sees everything they've chosen without scrolling the whole list.
 */
export function TagPicker({
  value,
  onChange,
  options,
  labelOf,
  max,
  customPlaceholder,
  collapsedCount = 14,
}: {
  value: string[];
  onChange: (v: string[]) => void;
  options: string[];
  labelOf: (k: string) => string;
  max?: number;
  customPlaceholder: string;
  collapsedCount?: number;
}) {
  const T = useT();
  const [custom, setCustom] = useState('');
  const [all, setAll] = useState(false);
  const extra = value.filter((v) => !options.includes(v));
  const visible = all ? options : options.slice(0, collapsedCount);
  const full = max !== undefined && value.length >= max;

  const toggle = (k: string) =>
    onChange(value.includes(k) ? value.filter((x) => x !== k) : full ? value : [...value, k]);

  function addCustom() {
    const tag = custom.trim().toLowerCase().replace(/\s+/g, ' ').slice(0, 30);
    if (!tag || value.includes(tag) || full) return setCustom('');
    onChange([...value, tag]);
    setCustom('');
  }

  return (
    <div>
      <div className="flex flex-wrap gap-2">
        {extra.map((k) => (
          <Chip key={k} on onClick={() => toggle(k)}>
            {labelOf(k)}
          </Chip>
        ))}
        {visible.map((k) => (
          <Chip key={k} on={value.includes(k)} onClick={() => toggle(k)}>
            {labelOf(k)}
          </Chip>
        ))}
        {options.length > collapsedCount && (
          <button
            type="button"
            className="btn-quiet min-h-[40px] px-2 text-sm"
            onClick={() => setAll((a) => !a)}
          >
            {all ? T('कम दिखाएँ', 'Show fewer') : T('सब दिखाएँ', 'Show all')}
          </button>
        )}
      </div>
      <div className="mt-3 flex gap-2">
        <input
          value={custom}
          onChange={(e) => setCustom(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addCustom();
            }
          }}
          placeholder={customPlaceholder}
          disabled={full}
        />
        <button
          type="button"
          className="btn-soft !w-auto shrink-0 px-4"
          onClick={addCustom}
          disabled={!custom.trim() || full}
        >
          <Icon name="plus" className="h-4 w-4" />
          {T('जोड़ें', 'Add')}
        </button>
      </div>
      {max !== undefined && (
        <p className="mt-1 text-xs text-muted">
          {value.length} / {max}
        </p>
      )}
    </div>
  );
}

/**
 * Choose a photo, shrink it on the phone, upload it, hand back the URL.
 * `shape` only changes how the preview looks.
 */
export function ImageUpload({
  value,
  onChange,
  shape,
  label,
}: {
  value: string | null | undefined;
  onChange: (url: string | null) => void;
  shape: 'round' | 'wide';
  label: string;
}) {
  const T = useT();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onFile(file: File) {
    setBusy(true);
    setError(null);
    try {
      const { dataUrl } = await compressImage(file);
      onChange(await uploadFile(dataUrl));
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : T('फ़ोटो नहीं गई। दोबारा करें।', 'Upload failed. Try again.'),
      );
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  }

  return (
    <div>
      <input
        ref={input}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void onFile(f);
        }}
      />
      <button
        type="button"
        onClick={() => input.current?.click()}
        disabled={busy}
        className={`group relative flex items-center justify-center overflow-hidden border-2 border-dashed border-line bg-panel-2 text-muted transition hover:border-[color:var(--sec-ink)] ${
          shape === 'round' ? 'h-24 w-24 rounded-full' : 'h-28 w-full rounded-2xl'
        }`}
        aria-label={label}
      >
        {value ? (
          <img src={value} alt="" className="absolute inset-0 h-full w-full object-cover" />
        ) : (
          <span className="flex flex-col items-center gap-1 text-xs font-semibold">
            <Icon name="camera" className="h-6 w-6" />
            {label}
          </span>
        )}
        {busy && (
          <span className="absolute inset-0 flex items-center justify-center bg-white/70 text-xs font-semibold text-text">
            {T('अपलोड…', 'Uploading…')}
          </span>
        )}
        {value && !busy && (
          <span className="absolute bottom-1.5 right-1.5 flex h-8 w-8 items-center justify-center rounded-full bg-white text-text shadow-card">
            <Icon name="camera" className="h-4 w-4" />
          </span>
        )}
      </button>
      {value && !busy && (
        <button type="button" className="btn-quiet mt-1 text-xs" onClick={() => onChange(null)}>
          {T('हटाएँ', 'Remove')}
        </button>
      )}
      {error && <p className="mt-1 text-xs text-bad">{error}</p>}
    </div>
  );
}

/**
 * Ask Lulu's AI to draft something. The result is shown, never applied on
 * its own: the creator reads it and decides — it's their voice on the line.
 */
export function AiHelper({
  task,
  input,
  label,
  applyLabel,
  onApply,
  disabled,
}: {
  task: 'bio' | 'pitch' | 'ideas';
  input: Record<string, unknown>;
  label: string;
  applyLabel?: string;
  onApply?: (text: string) => void;
  disabled?: boolean;
}) {
  const T = useT();
  const [busy, setBusy] = useState(false);
  const [text, setText] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  async function run() {
    setBusy(true);
    setError(null);
    setCopied(false);
    try {
      const r = await api.post<{ text?: string }>('/v1/partner/creator/ai', { task, input });
      setText(r.text?.trim() || null);
      if (!r.text?.trim())
        setError(T('AI ने कुछ नहीं लिखा। दोबारा करें।', 'The AI came back empty. Try again.'));
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : T('AI अभी उपलब्ध नहीं।', 'AI help is not available right now.'),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <button
        type="button"
        className="btn-soft"
        onClick={() => void run()}
        disabled={busy || disabled}
      >
        <Icon name="sparkle" className="h-5 w-5" />
        {busy
          ? T('लिख रहे हैं…', 'Writing…')
          : text
            ? T('दूसरा लिखें', 'Write another')
            : label}
      </button>
      {error && <p className="mt-2 text-xs text-bad">{error}</p>}
      {text && (
        <div
          className="mt-3 rounded-2xl border p-3"
          style={{ borderColor: 'var(--sec-ink)', background: 'var(--sec-wash)' }}
        >
          <p className="eyebrow mb-1" style={{ color: 'var(--sec-ink)' }}>
            {T('AI का सुझाव', 'AI suggestion')}
          </p>
          <p className="whitespace-pre-wrap text-sm leading-relaxed">{text}</p>
          <div className="mt-3 flex gap-2">
            {onApply && (
              <button
                type="button"
                className="btn-primary !min-h-[40px] text-sm"
                onClick={() => onApply(text)}
              >
                <Icon name="check" className="h-4 w-4" />
                {applyLabel ?? T('इसे लगाएँ', 'Use this')}
              </button>
            )}
            <button
              type="button"
              className="btn-secondary !min-h-[40px] text-sm"
              onClick={() => void copyText(text).then(setCopied)}
            >
              <Icon name="copy" className="h-4 w-4" />
              {copied ? T('कॉपी हो गया', 'Copied') : T('कॉपी', 'Copy')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/** A small coloured platform badge: "IG", "YT"… */
export function PlatformDot({
  platform,
  className = 'h-8 w-8 text-[11px]',
}: {
  platform: string;
  className?: string;
}) {
  const colour = PLATFORMS[platform]?.colour ?? '#64748b';
  const short: Record<string, string> = {
    instagram: 'IG',
    youtube: 'YT',
    facebook: 'FB',
    threads: '@',
    x: 'X',
    linkedin: 'in',
    sharechat: 'SC',
    moj: 'Mj',
    josh: 'Jo',
  };
  return (
    <span
      aria-hidden
      className={`flex shrink-0 items-center justify-center rounded-xl font-bold text-white ${className}`}
      style={{ background: colour }}
    >
      {short[platform] ?? '•'}
    </span>
  );
}

export function VerifiedTick({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <span
      className={`inline-flex shrink-0 items-center justify-center rounded-full bg-sky-500 text-white ${className}`}
      title="Verified"
    >
      <Icon name="check" className="h-[70%] w-[70%]" strokeWidth={3} />
    </span>
  );
}

/**
 * The card a brand sees in search, drawn from what's in the form right now.
 * Showing it live is the point: a creator writes a sharper headline when they
 * can see it sitting on the card next to their photo.
 */
export function CreatorCardPreview({
  profile,
  channels,
  fromPriceMicros,
}: {
  profile: Partial<CreatorProfile>;
  channels: CreatorChannel[];
  fromPriceMicros: string | null;
}) {
  const { lang } = useI18n();
  const T = useT();
  const total = channels.reduce((s, c) => s + (Number(c.followers) || 0), 0);
  const verified = channels.some(isVerified);
  const name = profile.displayName?.trim() || T('आपका नाम', 'Your name');
  const niches = profile.niches ?? [];
  return (
    <article className="overflow-hidden rounded-[22px] border border-line bg-panel shadow-card">
      <div
        className="relative h-24 bg-cover bg-center"
        style={
          profile.coverUrl
            ? { backgroundImage: `url("${profile.coverUrl}")` }
            : { backgroundImage: 'linear-gradient(135deg, var(--sec-from), var(--sec-to))' }
        }
      >
        {profile.creatorType && (
          <span className="absolute right-3 top-3 rounded-full bg-white/90 px-2.5 py-0.5 text-[11px] font-bold text-text">
            {profile.creatorType === 'ugc'
              ? T('UGC क्रिएटर', 'UGC creator')
              : profile.creatorType === 'both'
                ? T('इन्फ़्लुएंसर + UGC', 'Influencer + UGC')
                : T('इन्फ़्लुएंसर', 'Influencer')}
          </span>
        )}
      </div>
      <div className="px-4 pb-4">
        <div className="-mt-9 flex items-end gap-3">
          {profile.photoUrl ? (
            <img
              src={profile.photoUrl}
              alt=""
              className="h-[72px] w-[72px] shrink-0 rounded-full object-cover ring-4 ring-white"
            />
          ) : (
            <span
              className="flex h-[72px] w-[72px] shrink-0 items-center justify-center rounded-full font-display text-2xl font-bold text-white ring-4 ring-white"
              style={{ backgroundImage: 'linear-gradient(135deg, #f472b6, #7c3aed)' }}
            >
              {name.slice(0, 1).toUpperCase()}
            </span>
          )}
          <div className="min-w-0 pb-1">
            <p className="flex items-center gap-1 font-display text-lg font-bold leading-tight">
              <span className="truncate">{name}</span>
              {verified && <VerifiedTick />}
            </p>
            <p className="truncate text-xs text-muted">
              @{profile.handle || 'handle'}
              {profile.city ? ` · ${profile.city}` : ''}
            </p>
          </div>
        </div>
        <p className="mt-2 text-sm leading-snug">
          {profile.headline?.trim() || (
            <span className="text-muted">
              {T('एक लाइन जो बताए आप क्या बनाते हैं', 'One line on what you make')}
            </span>
          )}
        </p>
        {niches.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {niches.slice(0, 4).map((n) => (
              <span
                key={n}
                className="rounded-full bg-panel-2 px-2 py-0.5 text-[11px] font-semibold text-muted"
              >
                {nicheLabel(n, lang)}
              </span>
            ))}
            {niches.length > 4 && (
              <span className="px-1 text-[11px] text-muted">+{niches.length - 4}</span>
            )}
          </div>
        )}
        <div className="mt-3 flex items-center justify-between gap-2 border-t border-line pt-3">
          <div className="flex items-center gap-1">
            {channels.slice(0, 4).map((c) => (
              <PlatformDot key={c.id} platform={c.platform} className="h-6 w-6 text-[9px]" />
            ))}
            <span className="tnum ml-1 text-sm font-bold">{compactCount(total)}</span>
            <span className="text-xs text-muted">{T('फ़ॉलोअर', 'followers')}</span>
          </div>
          {fromPriceMicros ? (
            <span className="text-right text-xs text-muted">
              {T('शुरू', 'from')}{' '}
              <b className="tnum text-sm text-text">{formatINR(fromPriceMicros)}</b>
            </span>
          ) : (
            <span className="text-xs text-muted">{T('पैकेज जोड़ें', 'Add a package')}</span>
          )}
        </div>
        {channels.length > 0 && (
          <p className="sr-only">
            {channels
              .map((c) => `${platformLabel(c.platform, lang)} ${c.followers}`)
              .join(', ')}
          </p>
        )}
      </div>
    </article>
  );
}
